import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";
import RegisteredOccurrenceVehiclesService from "../../../services/reports/registered-occurrence-vehicles-service.js";

const initialFilter = () => ({
    plate: "",
    visibility: { label: "Todos", value: "ALL-OCC" },
    cityFact: [],
    archivedOccurrences: false,
    onlyMyCity: true,
});

const filtersLabels = {
    plate: "Placa",
    visibility: "Visibilidade",
    cityFact: "Município do fato",
    archivedOccurrences: "Contabilizar ocorrências arquivadas",
    onlyMyCity: "Apenas ocorrências da minha corporação",
};

export default function RegisteredOccurrenceVehicles() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [filters, setFilters] = useState(initialFilter);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Veículo cadastrado em ocorrência";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
            }, filtersLabels)
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            if (!filters.plate || !filters.plate.trim()) {
                ReportUtils.alertErrorMessage('O campo "Placa" é obrigatório.');
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                plate: filters.plate ?? "",
                visibility: filters.visibility.value ?? "",
                cityFact: Array.isArray(filters.cityFact) ? filters.cityFact.map(i => i.value) : [],
                archivedOccurrences: filters.archivedOccurrences ?? false,
                onlyMyCity: filters.onlyMyCity ?? false,
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
            }, formattedFilters, true);

            const response = await RegisteredOccurrenceVehiclesService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    const vehicleCount = reportData?.data?.vehicle ? 1 : 0;
    const occurrenceCount = reportData?.data?.occurrences?.length ?? 0;
    const totalReportItems = vehicleCount + occurrenceCount;

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={totalReportItems}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0!" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            clearSearch={clearSearch}
                            apply={apply}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || totalReportItems === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || totalReportItems === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body className={`flex-1 ${!reportData || totalReportItems === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                        <NotAppliedContent reportData={reportData} />
                        <NoDataContent reportData={reportData} data={totalReportItems} />
                        <Content
                            reportData={reportData}
                            data={reportData?.data}
                            entity={headerSelected}
                            title={title}
                            printComponentRef={printComponentRef}
                            setIsLoading={setIsLoading}
                        />
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}